/**
 * DateRangePicker Component
 * Provides preset date ranges and custom date selection for filtering logs
 */

import { useState } from 'react';
import { Calendar } from 'react-feather';
import { DateRange, DateRangePreset } from '../../types/logs';

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange, preset: DateRangePreset) => void;
}

const presets: { label: string; value: DateRangePreset }[] = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: 'last7days' },
  { label: 'Last 30 Days', value: 'last30days' },
  { label: 'This Month', value: 'thisMonth' },
  { label: 'Custom', value: 'custom' }
];

function getPresetRange(preset: DateRangePreset): DateRange {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case 'today':
      return { startDate: today, endDate: now };
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { startDate: yesterday, endDate: today };
    }
    case 'last7days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 7);
      return { startDate: start, endDate: now };
    }
    case 'last30days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return { startDate: start, endDate: now };
    }
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: start, endDate: now };
    }
    case 'lastMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: start, endDate: end };
    }
    case 'custom':
    default:
      return { startDate: null, endDate: null };
  }
}

function formatDate(date: Date | null): string {
  if (!date) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateForInput(date: Date | null): string {
  if (!date) return '';
  return date.toISOString().split('T')[0];
}

export default function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [activePreset, setActivePreset] = useState<DateRangePreset>('last7days');

  const handlePresetClick = (preset: DateRangePreset) => {
    setActivePreset(preset);
    if (preset === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
      const range = getPresetRange(preset);
      onChange(range, preset);
    }
  };

  const handleCustomDateChange = (field: 'startDate' | 'endDate', dateString: string) => {
    const date = dateString ? new Date(dateString) : null;
    const newRange = { ...value, [field]: date };
    onChange(newRange, 'custom');
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Preset buttons */}
      <div className="flex flex-wrap gap-2">
        {presets.map(({ label, value: preset }) => (
          <button
            key={preset}
            onClick={() => handlePresetClick(preset)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              activePreset === preset
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Current range display */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Calendar size={16} />
        <span>
          {value.startDate ? formatDate(value.startDate) : 'Start'}
          {' - '}
          {value.endDate ? formatDate(value.endDate) : 'End'}
        </span>
      </div>

      {/* Custom date inputs */}
      {showCustom && (
        <div className="flex gap-4 mt-2">
          <div className="flex flex-col">
            <label htmlFor="start-date" className="text-xs text-gray-500 mb-1">
              Start Date
            </label>
            <input
              id="start-date"
              type="date"
              value={formatDateForInput(value.startDate)}
              onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              aria-label="Start date"
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="end-date" className="text-xs text-gray-500 mb-1">
              End Date
            </label>
            <input
              id="end-date"
              type="date"
              value={formatDateForInput(value.endDate)}
              onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
              aria-label="End date"
            />
          </div>
        </div>
      )}
    </div>
  );
}
